import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';

import {
  buildLevelToTableMap,
  buildTableToLevelMap,
  flattenLevels,
  getOrphanTables,
  hasHierarchy,
  type SchemaDefinitionLike,
} from '../../lib/schemaTopology';
import HierarchyTreeNav, { type NavSelection } from './HierarchyTreeNav';
import LevelTableView from './LevelTableView';
import type { OutputLevel } from '../../lib/outputStructure';

export interface UnifiedSchemaBrowserProps {
  definition: SchemaDefinitionLike;
}

/**
 * Read-only unified schema view: hierarchy on the left, the selected
 * level's table on the right. Adapts automatically when the schema has
 * no hierarchy (becomes a flat list + table detail), no tables
 * (empty state), or a single table (auto-selected).
 */
const MIN_PANEL_WIDTH = 200;
const MAX_PANEL_WIDTH = 700;
const DEFAULT_PANEL_WIDTH = 320;

const UnifiedSchemaBrowser: React.FC<UnifiedSchemaBrowserProps> = ({ definition }) => {
  const tableNames = useMemo(() => Object.keys(definition?.tables || {}), [definition]);
  const levelToTable = useMemo(() => buildLevelToTableMap(definition), [definition]);
  const tableToLevel = useMemo(() => buildTableToLevelMap(definition), [definition]);
  const orphanTables = useMemo(() => getOrphanTables(definition), [definition]);
  const hasTree = hasHierarchy(definition);
  const flat = useMemo(
    () => (hasTree ? flattenLevels(definition.output_structure) : []),
    [hasTree, definition],
  );

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(DEFAULT_PANEL_WIDTH);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const next = dragStartWidth.current + (ev.clientX - dragStartX.current);
      setPanelWidth(Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, next)));
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  // Default selection: first level (when hierarchical) or first table.
  const [selection, setSelection] = useState<NavSelection>(() => {
    if (hasTree && flat.length > 0) {
      return { kind: 'level', pathKey: flat[0].pathKey };
    }
    if (tableNames.length > 0) {
      return { kind: 'table', tableName: tableNames[0] };
    }
    return null;
  });

  // Re-default when the definition swaps under us (e.g., re-interpret).
  useEffect(() => {
    if (selection) {
      if (selection.kind === 'level' && !levelToTable[selection.pathKey]) {
        // Selected level no longer exists.
      } else if (selection.kind === 'table' && !definition?.tables?.[selection.tableName]) {
        // Selected table no longer exists.
      } else {
        return;
      }
    }
    if (hasTree && flat.length > 0) {
      setSelection({ kind: 'level', pathKey: flat[0].pathKey });
    } else if (tableNames.length > 0) {
      setSelection({ kind: 'table', tableName: tableNames[0] });
    } else {
      setSelection(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);

  if (tableNames.length === 0 && !hasTree) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This schema hasn't been annotated yet. Run <strong>Annotate Schema</strong>
        to populate tables and the hierarchy.
      </Alert>
    );
  }

  // Resolve selection to a (table, level, breadcrumb) tuple for the right pane.
  let activeTable: string | null = null;
  let activeLevel: OutputLevel | null = null;
  let breadcrumb: string[] = [];
  if (selection?.kind === 'level') {
    activeTable = levelToTable[selection.pathKey] || null;
    const entry = flat.find((e) => e.pathKey === selection.pathKey);
    activeLevel = entry?.level || null;
    breadcrumb = entry?.path || [];
  } else if (selection?.kind === 'table') {
    activeTable = selection.tableName;
    breadcrumb = tableToLevel[selection.tableName] || [];
    if (breadcrumb.length > 0) {
      const entry = flat.find((e) => e.pathKey === breadcrumb.join('/'));
      activeLevel = entry?.level || null;
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', p: 2, flex: 1, minHeight: 0, gap: 0 }}>
      <HierarchyTreeNav
        outputStructure={definition?.output_structure}
        tableNames={tableNames}
        orphanTables={orphanTables}
        levelToTable={levelToTable}
        selected={selection}
        onSelect={setSelection}
        mode="view"
        width={panelWidth}
      />
      {/* Drag handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          width: 6,
          flexShrink: 0,
          cursor: 'col-resize',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'stretch',
          mx: 0.5,
          '&::after': {
            content: '""',
            display: 'block',
            width: 2,
            mx: 'auto',
            borderRadius: 1,
            bgcolor: 'divider',
            transition: 'background-color 0.15s',
          },
          '&:hover::after': { bgcolor: 'primary.main' },
        }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LevelTableView
          definition={definition}
          tableName={activeTable}
          level={activeLevel}
          breadcrumb={breadcrumb}
          mode="view"
        />
      </Box>
    </Box>
  );
};

export default UnifiedSchemaBrowser;
